<script setup lang="ts">
import { onMounted, ref } from "vue"
import { useI18n } from "vue-i18n"

import { AxiosError } from "axios"

import Button from "@/components/shared/Button.vue"
import Card from "@/components/shared/Card.vue"
import CardActions from "@/components/shared/CardActions.vue"
import CardContent from "@/components/shared/CardContent.vue"
import CardTitle from "@/components/shared/CardTitle.vue"
import InputBox from "@/components/shared/InputBox.vue"
import LayoutAuthed from "@/components/shared/LayoutAuthed.vue"
import router from "@/plugins/router"
import partnerService from "@/services/partner"
import { reportSentry } from "@/utils/sentry"

const { t } = useI18n()

const payload = ref<{ phones: string[]; emails: string[] }>({
  phones: [],
  emails: [],
})

const isSubmitting = ref(false)
const errorMessage = ref("")

async function saveItem() {
  isSubmitting.value = true
  errorMessage.value = ""

  try {
    const params = {
      phones: payload.value.phones.filter((p) => p.trim() !== "").filter((p, i, arr) => arr.indexOf(p) === i),
      emails: payload.value.emails.filter((e) => e.trim() !== "").filter((e, i, arr) => arr.indexOf(e) === i),
    }
    await partnerService.updateNotification(params)

    router.push(`/settings`)
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      errorMessage.value = t("shared.errors.unknown")
    }
    isSubmitting.value = false
  }
}

async function loadData() {
  try {
    const data = await partnerService.notification()

    payload.value.phones = data.phones.map((p) => p.number)
    payload.value.emails = data.emails
  } catch (error) {
    console.error(error)
    reportSentry(error)
  }
}

// initial load
onMounted(async () => {
  await loadData()
})
</script>

<template>
  <LayoutAuthed :title="$t('nav.settings')">
    <div class="row">
      <div class="col-12 col-lg-9 col-xxl-6">
        <form @submit.prevent="saveItem()">
          <Card>
            <CardTitle>{{ $t("partner.mobilePhone") }}</CardTitle>
            <CardContent>
              <div v-for="(_, index) in payload.phones" :key="index" class="row align-items-end py-2">
                <div class="col">
                  <InputBox
                    v-model="payload.phones[index]!"
                    type="text"
                    field="name"
                    :label="$t('partner.mobilePhone') + ' ' + (index + 1)"
                    :required="index === 0"
                  />
                </div>
                <div class="col-auto">
                  <Button
                    :class="{ invisible: index === 0 }"
                    type="button"
                    color="danger"
                    :text="$t('shared.delete')"
                    @click="payload.phones.splice(index, 1)"
                  ></Button>
                </div>
              </div>
              <Button
                v-if="payload.phones.length < 5"
                type="button"
                color="warning"
                :text="$t('shared.add')"
                @click="payload.phones.push('')"
              />
            </CardContent>
            <CardTitle>{{ $t("partner.email") }}</CardTitle>
            <CardContent>
              <div v-for="(_, index) in payload.emails" :key="index" class="row align-items-end py-2">
                <div class="col">
                  <InputBox
                    v-model="payload.emails[index]!"
                    type="email"
                    field="email"
                    :label="$t('partner.email') + ' ' + (index + 1)"
                    :required="index === 0"
                  />
                </div>
                <div class="col-auto">
                  <Button
                    :class="{ invisible: index === 0 }"
                    type="button"
                    color="danger"
                    :text="$t('shared.delete')"
                    @click="payload.emails.splice(index, 1)"
                  ></Button>
                </div>
              </div>
              <Button
                v-if="payload.emails.length < 5"
                type="button"
                color="warning"
                :text="$t('shared.add')"
                @click="payload.emails.push('')"
              />
            </CardContent>
            <CardActions displayBlock>
              <Button type="submit" color="dark" :text="$t('shared.save')" :isSubmitting="isSubmitting" />
              <span class="text-center text-danger error-message">{{ errorMessage }}</span>
            </CardActions>
          </Card>
        </form>
      </div>
    </div>
  </LayoutAuthed>
</template>

<style scoped></style>
